/**
 * 法律文档：注册协议（演示）+ 官方免责声明 / 隐私声明。
 * 免责声明、隐私声明正文与 App `legalDocs.generated.ts` 保持同步。
 */

import type { H5Lang } from '../../../i18n';
import { getLang } from '../../../i18n';
import type { LegalDoc, LegalDocId } from './legalDocTypes';
import {
  DISCLAIMER_DOC_EN,
  DISCLAIMER_DOC_TW,
  DISCLAIMER_DOC_ZH,
  PRIVACY_DOC_EN,
  PRIVACY_DOC_TW,
  PRIVACY_DOC_ZH,
} from './legalDocs.generated';

const APP_NAME = 'DIFI';

/** 与 App `legalDocsMock.AGREEMENT_DOC` 对齐；H5 路由仍用 `register` */
const REGISTER_DOC: LegalDoc = {
  id: 'register',
  navTitle: '注册协议',
  title: `${APP_NAME}用户注册协议`,
  effectiveDate: '2026-05-18',
  version: '',
  intro:
    '欢迎使用UX Securities Limited相关服务。本《注册协议》（下称「本协议」）由您与运营主体共同订立，请您在注册、登录或使用服务前仔细阅读并充分理解本协议全部内容。',
  sections: [
    {
      title: '一、总则',
      paragraphs: [
        '欢迎使用UX Securities Limited相关服务。本《注册协议》（下称「本协议」）由您与运营主体共同订立，请您在注册、登录或使用服务前仔细阅读并充分理解本协议全部内容。',
      ],
    },
    {
      title: '二、账户注册与使用',
      paragraphs: [
        '您应使用真实、准确、完整的信息完成注册，并妥善保管账户及密码。因您保管不善导致的损失，由您自行承担；我们可在法律法规允许范围内采取合理措施协助您保护账户安全。',
      ],
    },
    {
      title: '三、服务说明',
      paragraphs: [
        '我们向您提供行情资讯、交易及相关辅助功能。具体服务范围以产品实际展示及监管要求为准。我们有权根据业务需要、合规要求对功能进行升级、调整或暂停，并将通过合理方式通知您。',
      ],
    },
    {
      title: '四、用户行为规范',
      paragraphs: [
        '您不得利用本服务从事任何违法违规活动，包括但不限于洗钱、欺诈、操纵市场、侵犯他人合法权益等行为。我们有权对违规行为进行调查，并依法采取限制功能、冻结账户等措施。',
      ],
    },
    {
      title: '五、知识产权',
      paragraphs: [
        '本服务中的软件、界面、文案、商标及其他内容的知识产权归我们或相关权利人所有。未经书面许可，您不得复制、修改、传播或用于商业目的。',
      ],
    },
    {
      title: '六、免责与责任限制',
      paragraphs: [
        '因不可抗力、网络故障、第三方服务中断等非我们可控原因导致的服务中断或数据异常，我们将在合理范围内尽力恢复，但不承担由此产生的间接损失。法律法规另有规定的除外。',
      ],
    },
    {
      title: '七、协议变更与终止',
      paragraphs: [
        '我们可根据法律法规或业务需要修订本协议，并通过应用内公告等方式公布。若您继续使用服务，即视为接受修订后的协议。您可随时申请注销账户；我们亦可在符合法律规定的情形下终止向您提供服务。',
      ],
    },
    {
      title: '八、适用法律与争议解决',
      paragraphs: [
        '本协议的订立、效力、解释与争议解决适用中华人民共和国香港特别行政区法律（如业务规则另有约定的，从其约定）。因本协议产生的争议，双方应友好协商；协商不成的，提交有管辖权的法院或仲裁机构解决。',
      ],
    },
  ],
};

function resolveLegalLang(lang?: string): H5Lang {
  const l = (lang ?? getLang()).toLowerCase();
  if (l.startsWith('en')) return 'en';
  if (l.startsWith('zh-tw') || l.startsWith('zh-hk') || l.includes('hant')) return 'zh-TW';
  if (l === 'zh-cn' || l.startsWith('zh')) return 'zh-CN';
  return getLang();
}

export function getLegalDoc(id: LegalDocId, lang?: string): LegalDoc | undefined {
  if (id === 'register') return REGISTER_DOC;

  const resolved = resolveLegalLang(lang);

  if (id === 'disclaimer') {
    if (resolved === 'en') return { ...DISCLAIMER_DOC_EN, id: 'disclaimer' };
    if (resolved === 'zh-TW') return { ...DISCLAIMER_DOC_TW, id: 'disclaimer' };
    return { ...DISCLAIMER_DOC_ZH, id: 'disclaimer' };
  }

  if (id === 'privacy') {
    if (resolved === 'en') return { ...PRIVACY_DOC_EN, id: 'privacy' };
    if (resolved === 'zh-TW') return { ...PRIVACY_DOC_TW, id: 'privacy' };
    return { ...PRIVACY_DOC_ZH, id: 'privacy' };
  }

  return undefined;
}

export type { LegalDoc, LegalDocId, LegalSection } from './legalDocTypes';
